<template>
    <reo-dialog
        class="available-countries-dialog"
        :labelOfConfirm="$t( 'homepage.compatibility.notify' )"
        @confirm='handleConfirm'
        @cancel='handleCancel'
        isTitleCentered
    >
        <template v-slot:countrySearch>
            <h3 class="not-available">{{ $t( 'subscribe.not_available_in_region' ) }}</h3>
            <h3>{{ $t('homepage.compatibility.countryTitle') }}</h3>
            <div class='countrySearch'>
                <div class="item-list-search">
                    <input type="text" v-model="searchText" :placeholder="$tc('itemList.search')">
                    <span class="icon-icon_search-2"></span>
                </div>
                <reo-select
                    v-if="!isInSearch"
                    :options="continentOptions"
                    :default-selected="continentOptions[0]?.value"
                    :widthOut='selectorStyle.widthOut'
                    :heightOut='selectorStyle.heightOut'
                    :widthIn='selectorStyle.widthIn'
                    :heightIn='selectorStyle.heightIn'
                    :selectMarginLeft='selectorStyle.selectMarginLeft'
                    :triangleRight='selectorStyle.triangleRight'
                    :maxHeight='selectorStyle.maxHeight'
                    :fontSizeIn='selectorStyle.fontSizeIn'
                    :fontSizeOut='selectorStyle.fontSizeOut'
                    @input="selectedContinent = $event"
                    colorType="dark"
                />
                <ul class="country-list">
                    <li v-for="country in showingCountries" :key="country.code">
                        {{ country.text }}
                        <div class="storage-region">
                            ({{ country.storageRegion }})
                        </div>
                    </li>
                </ul>
            </div>
            <p class='notice'>{{ $t( 'homepage.compatibility.notice' ) }}</p>
        </template>
    </reo-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoDialog from '@/basic/Dialog.vue';
import countryData from 'CONFIG/countryData.json';
import continentData from 'CONFIG/continentData.json';
import { ICloudProducts, ICountryItem, IReoSelectOption, ISelectorStyle, IStorageLoc } from '@reolink-cloud/interface';
import ReoSelect from '@/basic/ReoSelect.vue';
import { mapGetters } from 'vuex';

interface ICountryWithExtraData extends ICountryItem {
    continent: string;
    storageRegion: string;
}

const countryCodeToContinent = ( code: string ): string => {

    return countryData[code] ?? '';

};

export default Vue.extend({
    name: 'AvailableCountriesDialog',
    components: {
        ReoDialog,
        ReoSelect
    },
    props: {
        supportedCountryList: Array as PropType<ICountryItem[]>,
        storageLocList: Object as PropType<Record<string, IStorageLoc>>,
        cloudProductLists: Object as PropType<ICloudProducts>
    },
    methods: {
        handleConfirm() {

            this.$emit( 'confirm' );

        },

        handleCancel() {

            this.$emit( 'cancel' );

        },
        
        countryCodeToStorageRegion( code: string ): string {

            const storageRegionKey = Object.entries( this.storageLocList ?? {}).filter(( [, loc] ) => loc.supportedCountryList.some( item => item.code === code )).sort(( [keyA], [keyB] ) => {

                const keyASortOrder = this.cloudProductLists.storageRegions[keyA].sortOrder ?? 0;
                const keyBSortOrder = this.cloudProductLists.storageRegions[keyB].sortOrder ?? 0;
                return keyASortOrder - keyBSortOrder;
            
            })[0]?.[0] ?? Object.keys( this.storageLocList ?? {})[0];
            
            return this.cloudProductLists.storageRegions[storageRegionKey]?.i18n.name;

        }
    },
    data() {

        return {

            selectedContinent: continentData[0].code,
            searchText: ''

        };

    },
    computed: {
        ...mapGetters( ['isMobile'] as const ),
        continentOptions(): IReoSelectOption[] {

            return Array.from( continentData ).filter( item => this.countriesWithExtraData.some( country => country.continent === item.code ))
                .map(( item ) => ({

                    label: item.name[ this.$i18n.locale as keyof typeof item.name ] || item.name.en,
                    value: item.code,
                    id: item.code

                }));

        },
        selectorStyle (): ISelectorStyle {

            return !this.isMobile ? {
                widthOut: 3.62,
                heightOut: 0.4,
                widthIn: 3.62,
                heightIn: 0.4,
                selectMarginLeft: 0.12,
                triangleRight: 0.12,
                maxHeight: 3.08,
                fontSizeIn: 0.14,
                fontSizeOut: 0.14
            } : {
                widthOut: -1,
                heightOut: 0.36,
                widthIn: -1,
                heightIn: 0.46,
                selectMarginLeft: 0.12,
                triangleRight: 0.12,
                maxHeight: 4.75,
                fontSizeIn: 0.17,
                fontSizeOut: 0.14
            };

        },
        countriesWithExtraData(): ICountryWithExtraData[] {

            return this.supportedCountryList.map(( item ) => ({

                ...item,
                continent: countryCodeToContinent( item.code ),
                storageRegion: this.countryCodeToStorageRegion( item.code )

            }));
        
        },
        showingCountries(): ICountryWithExtraData[] {

            if ( this.isInSearch ) {

                return this.countriesWithExtraData.filter(( item ) => item.text.toLowerCase().includes( this.searchText.trim().toLowerCase()));
            
            }

            if ( this.selectedContinent ) {

                return this.countriesWithExtraData.filter(( item ) => item.continent === this.selectedContinent );
            
            }
            return this.countriesWithExtraData;
        
        },
        isInSearch(): boolean {
 
            return this.searchText.trim().length > 0;
        
        }
    }

});
</script>

<style lang="less" scoped>
    .countrySearch{
        flex-shrink: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        .country-list{
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-auto-rows: min-content;
            gap: 12px;
            overflow-y: auto;
            margin-top: 12px;
            height: 300px;
            .mobile({
                grid-template-columns: repeat(2, 1fr);
                height: auto;
                max-height: 100%;
            });
            li{
                padding: 8px 12px;
                font-size: 16px;
                font-weight: 500;
                .storage-region{
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: @font_secondary;
                    font-size: 12px;
                    margin-top: 2px;
                }
            }
        }
    }
    .notice{
        border-top: 1px solid #ddd;
        line-height: 1.4;
        color:#333;

        @media @pc {
            font-size: .16rem;
            margin: .12rem 0 0;
            padding-top: .24rem;
            text-align: left;
        }
        @media @mobile{
            font-size: .26rem;
            padding-top: .4rem;
        }
    }
    .reo-dialog.available-countries-dialog{
        /deep/ .reo-dialog-inner{
            @media @pc{
                width: 1000px;
                max-width: unset;
            }
            .mobile({
                padding-bottom: 16px;
            });
        }
    }
    
    .item-list-search{
        width: 100%;
        margin:0 auto;
        position: relative;
        border-radius: 5px;
        margin-bottom: 12px;
        input{
            box-shadow: none;
            outline: none;
            box-shadow: none;
            padding: 0;
            height: 48px;
            box-sizing: border-box;
            width: 100%;
            font-size: 18px;
            border-radius: @border-radius-level-2;
            padding: 14px 12px;
            border: 1px solid @font_tertiary;
            .mobile({
                height: 37px;
                font-size: 14px;
            });
        }
        span{
            color:@font_secondary;
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 24px;
            .mobile({
                font-size: 16px;
            });
        }
    }
    
    h3{
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 31px;
        line-height: 1.2;
        .mobile({
            font-size: 20px;
            margin-bottom: 12px;
        });
        &.not-available{
            font-size: 18px;
            font-weight: 500;
            .mobile({
                font-size: 14px;
                font-weight: 500;
                padding-bottom: 12px;
                border-bottom: 1px solid @line;
                margin-bottom: 12px;
            });
        }
    }

</style>
